import React, { useEffect, useState, useRef } from 'react';
import { useChat, useTrackTranscription, useLocalParticipant } from '@livekit/components-react';
import { Track, LocalParticipant } from 'livekit-client';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { submitTranscribe } from '../../../redux/action';

const PracticeQuestion = ({
    agentAudioTrack,
    isBeam = false,
    practiceStatus = false,
    transcriptDetails = '',
    updateSocket,
}) => {
    const dispatch = useDispatch();
    const { roomId } = useParams();
    const agentMessages = useTrackTranscription(agentAudioTrack || undefined);
    const localParticipant = useLocalParticipant();
    const localMessages = useTrackTranscription({
        publication: localParticipant.microphoneTrack,
        source: Track.Source.Microphone,
        participant: localParticipant.localParticipant,
    });

    const [transcripts, setTranscripts] = useState(new Map());
    const [messages, setMessages] = useState([]);
    const { chatMessages } = useChat();

    useEffect(() => {
        if (agentAudioTrack) {
            agentMessages.segments.forEach((s) =>
                transcripts.set(s.id, segmentToChatMessage(s, transcripts.get(s.id), agentAudioTrack.participant))
            );
        }
        localMessages.segments.forEach((s) =>
            transcripts.set(s.id, segmentToChatMessage(s, transcripts.get(s.id), localParticipant.localParticipant))
        );
        const allMessages = Array.from(transcripts.values());
        for (const msg of chatMessages) {
            const isAgent = agentAudioTrack
                ? msg.from?.identity === agentAudioTrack.participant?.identity
                : msg.from?.identity !== localParticipant.localParticipant.identity;
            const isSelf = msg.from?.identity === localParticipant.localParticipant.identity;
            let name = msg.from?.name;
            if (!name) {
                if (isAgent) name = 'Agent';
                else if (isSelf) name = 'You';
                else name = 'Unknown';
            }
            allMessages.push({
                name,
                message: msg.message,
                timestamp: msg.timestamp,
                isSelf: isSelf,
            });
        }
        allMessages.sort((a, b) => a.timestamp - b.timestamp);
        setMessages(allMessages);
    }, [
        transcripts,
        chatMessages,
        localParticipant.localParticipant,
        agentAudioTrack?.participant,
        agentMessages.segments,
        localMessages.segments,
        agentAudioTrack,
    ]);

    const messagesEndRef = useRef(null);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
        if (!practiceStatus && messages.length > 0) {
            updateSocket('allMessages', messages);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages]);

    useEffect(() => {
        console.log('[Effect] Messages updated:', messages);

        if (Array.isArray(messages) && messages.length > 0 && !isBeam && !practiceStatus) {
            const filteredMessages = messages.filter(
                (message) => message && ['Agent', 'You'].includes(message.name) && typeof message.message === 'string'
            );

            const groupedMessages = [];
            for (let i = 0; i < filteredMessages.length; i++) {
                const current = filteredMessages[i];
                const last = groupedMessages[groupedMessages.length - 1];

                if (last && last.name === current.name) {
                    last.message += ' ' + current.message.trim();
                } else {
                    groupedMessages.push({ ...current, message: current.message.trim() });
                }
            }

            const formattedMessages = groupedMessages.map(
                (message) => `${message.name === 'You' ? 'Candidate: ' : 'Interviewer: '}${message.message}`
            );

            if (formattedMessages.length > 0) {
                const generatedTranscript = formattedMessages.join('\n');
                const finalTranscript = transcriptDetails
                    ? `${transcriptDetails.trim()}\n${generatedTranscript}`
                    : generatedTranscript;

                dispatch(submitTranscribe({ roomId, transcript: finalTranscript }));
            }
        }
    }, [messages, dispatch, roomId, isBeam, practiceStatus, transcriptDetails]);

    return messages.length > 0 ? (
        <div className="sidebar-container-question" style={{ height: isBeam ? '89%' : 'auto%' }}>
            <div
                className="question-timer-container"
                style={{
                    height: isBeam ? '70vh' : '70%',
                    overflowY: 'auto',
                    padding: '10px',
                    position: 'relative',
                }}
            >
                {(() => {
                    const groupedMessages = [];
                    const filteredMessages = messages.filter(
                        (message) =>
                            message && ['Agent', 'You'].includes(message.name) && typeof message.message === 'string'
                    );
                    for (let i = 0; i < filteredMessages.length; i++) {
                        const current = filteredMessages[i];
                        const last = groupedMessages[groupedMessages.length - 1];
                        if (last && last.name === current.name) {
                            last.message += ' ' + current.message;
                        } else {
                            groupedMessages.push({ ...current });
                        }
                    }
                    return groupedMessages.map((msg, index) => (
                        <div key={index} style={{ padding: '5px 0' }}>
                            <b>{msg.name === 'You' ? 'You: ' : 'Interviewer: '}</b>
                            {msg.message}
                        </div>
                    ));
                })()}
                <div ref={messagesEndRef} />
            </div>
        </div>
    ) : (
        <div className="sidebar-container-question" style={{ height: isBeam ? '89%' : 'auto%' }}>
            <div className="question-timer-container">
                <p>Connecting...</p>
            </div>
        </div>
    );
};

function segmentToChatMessage(s, existingMessage, participant) {
    const msg = {
        message: s.final ? s.text : `${s.text} ...`,
        name: participant instanceof LocalParticipant ? 'You' : 'Agent',
        isSelf: participant instanceof LocalParticipant,
        timestamp: existingMessage?.timestamp ?? Date.now(),
    };
    return msg;
}

export default PracticeQuestion;
